"use client";

import { ImgComparisonSlider } from "@img-comparison-slider/react";
import type { HTMLImgComparisonSliderElement } from "@img-comparison-slider/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, ComponentProps, CSSProperties } from "react";

import { Image, Text } from "@/components/primitives";
import { useTranslation } from "@/core/i18n";
import { cn } from "@/core/utils";

export type BeforeAfterSliderImageFit = "contain" | "cover";

export type BeforeAfterSliderLabelMode = "edgeFade" | "directional";

/** Movement below this (in slider %) per frame is jitter — no tag flip. */
const DIRECTIONAL_MOTION_THRESHOLD_PCT = 0.15;
/** Idle after the last movement before both directional tags fade back out. */
const DIRECTIONAL_TAG_IDLE_FADE_MS = 520;

type BeforeAfterSliderProps = {
  beforeImage: string;
  afterImage: string;
  beforeLabel: string;
  afterLabel: string;
  className?: string;
  /** Applied to both before/after label chips — position/tone overrides win via twMerge. */
  labelClassName?: string;
  /** Font weight for both label chips. */
  labelWeight?: ComponentProps<typeof Text>["weight"];
  /**
   * "edgeFade" (default): both labels visible, fading out near their edge.
   * "directional": homepage-gallery behavior — only the label on the side
   * being revealed shows while the divider moves, and both fade out shortly
   * after motion stops.
   */
  labelMode?: BeforeAfterSliderLabelMode;
  bottomLabelClassName?: string;
  centerLabel?: string;
  centerLabelClassName?: string;
  /** @deprecated Prefer `imageAreaClassName` to style the comparison surface. */
  backgroundClassName?: string;
  imageAreaClassName?: string;
  imageFit?: BeforeAfterSliderImageFit;
  labelVerticalPosition?: "top" | "bottom";
  /** @deprecated Prefer `imageFit="contain"` for explicit layout behavior. */
  containImage?: boolean;
  showUtilityBar?: boolean;
  handleVerticalPosition?: `${number}%`;
  /** CSS `object-position` applied to both before and after images (e.g. "center top"). */
  imageObjectPosition?: string;
};

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel,
  afterLabel,
  className,
  labelClassName,
  labelWeight = "bold",
  labelMode = "edgeFade",
  bottomLabelClassName,
  centerLabel,
  centerLabelClassName,
  backgroundClassName,
  imageAreaClassName,
  imageFit,
  labelVerticalPosition = "top",
  containImage = false,
  showUtilityBar = false,
  handleVerticalPosition = "75%",
  imageObjectPosition,
}: BeforeAfterSliderProps) {
  const { t } = useTranslation();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [visibleDirectionalTag, setVisibleDirectionalTag] = useState<"before" | "after" | null>(
    null,
  );
  const [isManualTagMode, setIsManualTagMode] = useState(false);
  const sliderRef = useRef<HTMLImgComparisonSliderElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  // Directional tag visibility (homepage-gallery port): watch the web
  // component's value every frame — the auto-sweep writes `slider.value`
  // directly without emitting slide events, so polling is the only hook that
  // sees BOTH auto and manual motion. Moving right (value increasing)
  // reveals the AFTER side, so that tag shows; moving left shows BEFORE;
  // both fade out once motion stops. Manual mode (finger down) drops the
  // slow considered fade for a near-instant one so tags track the drag.
  useEffect(() => {
    if (labelMode !== "directional") {
      return;
    }

    let frameId: number | null = null;
    let idleTimerId: ReturnType<typeof setTimeout> | null = null;
    let lastPct: number | null = null;

    const watch = () => {
      const rawValue = Number(sliderRef.current?.value ?? Number.NaN);
      if (Number.isFinite(rawValue)) {
        if (lastPct === null) {
          lastPct = rawValue;
        }
        const delta = rawValue - lastPct;
        if (Math.abs(delta) >= DIRECTIONAL_MOTION_THRESHOLD_PCT) {
          setVisibleDirectionalTag(delta > 0 ? "after" : "before");
          lastPct = rawValue;
          if (idleTimerId !== null) clearTimeout(idleTimerId);
          idleTimerId = setTimeout(() => {
            setVisibleDirectionalTag(null);
            idleTimerId = null;
          }, DIRECTIONAL_TAG_IDLE_FADE_MS);
        }
      }
      frameId = requestAnimationFrame(watch);
    };
    frameId = requestAnimationFrame(watch);

    const endManualMode = () => setIsManualTagMode(false);
    window.addEventListener("pointerup", endManualMode);
    window.addEventListener("pointercancel", endManualMode);

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      if (idleTimerId !== null) clearTimeout(idleTimerId);
      window.removeEventListener("pointerup", endManualMode);
      window.removeEventListener("pointercancel", endManualMode);
    };
  }, [labelMode]);
  const resolvedImageFit = imageFit ?? (containImage ? "contain" : "cover");
  const imageClassName = cn(
    "block h-full w-full select-none",
    resolvedImageFit === "contain" ? "object-contain" : "object-cover",
  );
  const handleSlide = (event: ChangeEvent<HTMLImgComparisonSliderElement>) => {
    const nextPosition = Number(event.currentTarget.value ?? 50);
    if (Number.isFinite(nextPosition)) {
      setSliderPosition(nextPosition);
    }
  };

  /**
   * Track pointer-down position so we can distinguish a tap/click from a drag.
   * Uses the capture phase because the inner web-component may stop propagation
   * on pointer events during its own drag handling.
   */
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    setIsManualTagMode(true);
  }, []);

  /**
   * When the user clicks (pointer moved < 8 px in any direction) snap the
   * slider divider to the click's horizontal position.  This eliminates
   * "dead click" frustration where users tap/click the comparison images
   * and nothing happens because the component only handled drag gestures.
   */
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;

    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx > 8 || dy > 8) return; // Pointer moved — was a drag, not a click.

    const slider = sliderRef.current;
    if (!slider) return;

    const rect = slider.getBoundingClientRect();
    if (rect.width === 0) return;

    const percent = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const clamped = Math.max(0, Math.min(100, percent));

    slider.value = clamped;
    setSliderPosition(clamped);
  }, []);

  const sliderStyle = {
    "--default-handle-color": "hsl(var(--muted-foreground))",
    "--default-handle-width": "40px",
    "--divider-color": "hsl(var(--overlay-foreground))",
    "--divider-width": "2px",
    "--handle-position-start": handleVerticalPosition,
    "cursor": "ew-resize",
  } as CSSProperties;

  const beforeLabelOpacity =
    labelMode === "directional"
      ? visibleDirectionalTag === "before"
        ? 1
        : 0
      : getLabelOpacity(sliderPosition, "right");
  const afterLabelOpacity =
    labelMode === "directional"
      ? visibleDirectionalTag === "after"
        ? 1
        : 0
      : getLabelOpacity(sliderPosition, "left");
  // Homepage tag cadence: a considered 0.7s ease-out with a short lead-in on
  // the auto sweep, flipping to a near-instant fade under a finger drag.
  const labelTransitionClassName =
    labelMode === "directional"
      ? isManualTagMode
        ? "duration-[120ms]"
        : "duration-700 ease-out delay-[180ms]"
      : undefined;

  useEffect(() => {
    const sliderElement = sliderRef.current;
    if (!sliderElement) return;

    // The React wrapper can assign `value` before the web component upgrades,
    // in which case the divider parks at 0 on first paint. Re-assert the
    // 50/50 start once after mount; this runs before any user interaction.
    if (Number(sliderElement.value ?? 0) === 0) {
      sliderElement.value = 50;
    }

    if (!sliderElement.shadowRoot) return;

    const styleAttribute = "data-ageless-before-after-shadow-fix";
    const existingStyle = sliderElement.shadowRoot.querySelector(`[${styleAttribute}]`);
    if (existingStyle) return;

    const shadowStyle = document.createElement("style");
    shadowStyle.setAttribute(styleAttribute, "true");
    shadowStyle.textContent = ".second { height: 100%; } :host { cursor: ew-resize; }";
    sliderElement.shadowRoot.appendChild(shadowStyle);

    return () => {
      shadowStyle.remove();
    };
  }, []);

  return (
    <div
      className={cn(
        "relative mx-auto flex h-full w-full flex-col overflow-hidden rounded-2xl",
        className,
      )}
      data-dd-action-name="Before/After comparison slider"
    >
      <div
        className={cn(
          "bg-muted relative min-h-0 w-full flex-1 overflow-hidden",
          imageAreaClassName ?? backgroundClassName,
        )}
        onClick={handleClick}
        onPointerDownCapture={handlePointerDown}
      >
        <ImgComparisonSlider
          className="block h-full w-full"
          handle={false}
          ref={sliderRef}
          style={sliderStyle}
          value={50}
          onSlide={handleSlide}
        >
          <div
            className="bg-card ring-overlay/10 flex items-center justify-center rounded-full shadow-md ring-1"
            slot="handle"
            style={{
              height: "var(--default-handle-width)",
              width: "var(--default-handle-width)",
            }}
          >
            <svg
              aria-hidden="true"
              className="text-overlay block h-4 w-6"
              viewBox="-8 -3 16 6"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M -5 -2 L -7 0 L -5 2 M 5 -2 L 7 0 L 5 2"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
          <Image
            unoptimized
            alt={beforeLabel}
            className={imageClassName}
            draggable={false}
            height={400}
            slot="first"
            src={beforeImage}
            style={imageObjectPosition ? { objectPosition: imageObjectPosition } : undefined}
            width={400}
          />
          <Image
            unoptimized
            alt={afterLabel}
            className={imageClassName}
            draggable={false}
            height={400}
            slot="second"
            src={afterImage}
            style={imageObjectPosition ? { objectPosition: imageObjectPosition } : undefined}
            width={400}
          />
        </ImgComparisonSlider>

        <div
          className={cn(
            "pointer-events-none absolute left-3 rounded-md bg-black/55 px-2 py-1 text-white/85 backdrop-blur-sm transition-opacity duration-75",
            labelVerticalPosition === "top" ? "top-3" : "bottom-3",
            labelVerticalPosition === "bottom" ? bottomLabelClassName : undefined,
            labelTransitionClassName,
            labelClassName,
          )}
          style={{ opacity: beforeLabelOpacity }}
        >
          <Text component="span" text={beforeLabel} variant="caption" weight={labelWeight} />
        </div>
        <div
          className={cn(
            "pointer-events-none absolute right-3 rounded-md bg-black/55 px-2 py-1 text-white/85 backdrop-blur-sm transition-opacity duration-75",
            labelVerticalPosition === "top" ? "top-3" : "bottom-3",
            labelVerticalPosition === "bottom" ? bottomLabelClassName : undefined,
            labelTransitionClassName,
            labelClassName,
          )}
          style={{ opacity: afterLabelOpacity }}
        >
          <Text component="span" text={afterLabel} variant="caption" weight={labelWeight} />
        </div>
        {centerLabel && (
          <div
            className={cn(
              "pointer-events-none absolute left-1/2 z-[1] w-[70%] max-w-md -translate-x-1/2 rounded-lg bg-white/10 px-3 py-1.5 text-center text-white/90 backdrop-blur-sm",
              labelVerticalPosition === "top" ? "top-3" : "bottom-3",
              labelVerticalPosition === "bottom" ? bottomLabelClassName : undefined,
              centerLabelClassName,
            )}
          >
            <Text component="span" text={centerLabel} tone="inherit" variant="caption" />
          </div>
        )}
      </div>

      {showUtilityBar && (
        <div className="border-border bg-card/95 shrink-0 border-t px-3 py-1.5 backdrop-blur-md md:px-4 md:py-2">
          <div className="flex items-center justify-center gap-3">
            <Text component="p" text={t("common.dragToCompare")} tone="muted" variant="caption" />
          </div>
          <div className="mt-1 text-center">
            <Text
              component="p"
              text={t("common.aiSimulationOnly")}
              tone="muted"
              variant="caption"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function getLabelOpacity(position: number, side: "left" | "right"): number {
  const clampedPosition = Math.max(0, Math.min(100, position));
  const distanceFromEdge = side === "left" ? 100 - clampedPosition : clampedPosition;
  const fadeStartDistance = 2;
  const fadeEndDistance = 50;

  if (distanceFromEdge <= fadeStartDistance) {
    return 0;
  }

  if (distanceFromEdge >= fadeEndDistance) {
    return 1;
  }

  const progress = (distanceFromEdge - fadeStartDistance) / (fadeEndDistance - fadeStartDistance);

  return Math.round(progress * progress * (3 - 2 * progress) * 100) / 100;
}
